/**
 * Karten-Ankauf – Formular-Logik (sections/ankauf-form.liquid)
 *
 * - Kartentabelle mit dynamischen Zeilen
 * - CSV-Import (Moxfield, Deckbox, ManaBox, Archidekt, eigene Listen)
 * - Validierung mit Fehlerübersicht (WCAG: role="alert", aria-invalid)
 * - Versand als JSON an die Google-Apps-Script-Web-App
 *
 * Keine externen Abhängigkeiten.
 */
(() => {
  if (customElements.get('ankauf-form')) return;

  const MAX_CSV_BYTES = 2 * 1024 * 1024;
  const CONDITIONS = ['MT', 'NM', 'EX', 'GD', 'LP', 'PL', 'PO'];
  const LANGUAGES = ['en', 'de', 'fr', 'it', 'es', 'pt', 'ja', 'ko', 'ru', 'zhs', 'zht'];
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const SCRYFALL_API = 'https://api.scryfall.com';
  const SUGGEST_DELAY_MS = 250;
  const SUGGEST_MIN_CHARS = 2;
  const COLLECTION_BATCH = 75;
  const IMAGE_DELAY_MS = 400;
  const FILTER_DELAY_MS = 350;
  const COLLAPSE_THRESHOLD = 10;
  const COLLAPSED_VISIBLE = 3;

  /** Spaltennamen verschiedener Exporte → internes Feld */
  const HEADER_ALIASES = {
    qty: ['count', 'quantity', 'qty', 'anzahl', 'menge', 'amount'],
    name: ['name', 'card name', 'card', 'kartenname', 'karte'],
    set: ['set code', 'edition code', 'set', 'edition', 'setcode', 'set_code'],
    cn: ['collector number', 'card number', 'collector_number', 'number', 'nr', 'nummer', 'sammlernummer', 'cn'],
    condition: ['condition', 'zustand'],
    lang: ['language', 'lang', 'sprache'],
    finish: ['foil', 'finish', 'printing']
  };

  /** Zustände aus Exporten (US- und EU-Bezeichnungen) → Cardmarket-Skala */
  const CONDITION_MAP = {
    mt: 'MT', m: 'MT', mint: 'MT',
    nm: 'NM', 'near mint': 'NM', near_mint: 'NM', 'nearmint': 'NM',
    ex: 'EX', excellent: 'EX', 'lightly played': 'EX', lightly_played: 'EX', sp: 'EX', 'slightly played': 'EX',
    gd: 'GD', good: 'GD', 'moderately played': 'GD', moderately_played: 'GD', mp: 'GD', 'good (lightly played)': 'GD',
    lp: 'LP', 'light played': 'LP', light_played: 'LP',
    pl: 'PL', played: 'PL', 'heavily played': 'PL', heavily_played: 'PL', hp: 'PL',
    po: 'PO', poor: 'PO', damaged: 'PO', dmg: 'PO'
  };

  const LANGUAGE_MAP = {
    en: 'en', english: 'en', englisch: 'en',
    de: 'de', german: 'de', deutsch: 'de',
    fr: 'fr', french: 'fr', französisch: 'fr', franzoesisch: 'fr',
    it: 'it', italian: 'it', italienisch: 'it',
    es: 'es', sp: 'es', spanish: 'es', spanisch: 'es',
    pt: 'pt', portuguese: 'pt', portugiesisch: 'pt',
    ja: 'ja', jp: 'ja', japanese: 'ja', japanisch: 'ja',
    ko: 'ko', kr: 'ko', korean: 'ko', koreanisch: 'ko',
    ru: 'ru', russian: 'ru', russisch: 'ru',
    zhs: 'zhs', cs: 'zhs', 'chinese simplified': 'zhs', 'simplified chinese': 'zhs', 's-chinese': 'zhs',
    zht: 'zht', ct: 'zht', 'chinese traditional': 'zht', 'traditional chinese': 'zht', 't-chinese': 'zht'
  };

  /**
   * @param {string} template
   * @param {Record<string, string|number>} values
   * @returns {string}
   */
  const format = (template, values) => {
    let result = template;
    for (const [key, value] of Object.entries(values)) {
      result = result.split(`[${key}]`).join(String(value));
    }
    return result;
  };

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  /** Doppelseitige Karten: Scryfall findet sie per Name nur über die Vorderseite. */
  const frontName = (name) => String(name || '').split(' // ')[0].trim();

  /** Kleines Kartenbild (146 × 204 px); bei doppelseitigen Karten die Vorderseite. */
  const smallImage = (card) =>
    (card && card.image_uris && card.image_uris.small) ||
    (card && Array.isArray(card.card_faces) && card.card_faces[0] && card.card_faces[0].image_uris && card.card_faces[0].image_uris.small) ||
    '';

  /**
   * Robuster CSV-Parser (Anführungszeichen, Zeilenumbrüche in Feldern,
   * Trennzeichen , ; oder Tab werden automatisch erkannt).
   * @param {string} text
   * @returns {string[][]}
   */
  const parseCsv = (text) => {
    const clean = text.replace(/^\uFEFF/, '');
    const firstLine = clean.split(/\r?\n/, 1)[0] || '';
    const counts = { ',': 0, ';': 0, '\t': 0 };
    for (const char of firstLine) {
      if (char in counts) counts[char] += 1;
    }
    const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < clean.length; i += 1) {
      const char = clean[i];
      if (inQuotes) {
        if (char === '"' && clean[i + 1] === '"') {
          field += '"';
          i += 1;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          field += char;
        }
        continue;
      }
      if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        row.push(field);
        field = '';
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && clean[i + 1] === '\n') i += 1;
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else {
        field += char;
      }
    }
    if (field !== '' || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  };

  /**
   * Ordnet Kopfzeilen den internen Feldern zu.
   * @param {string[]} header
   * @returns {Record<string, number>}
   */
  const mapHeader = (header) => {
    const normalized = header.map(normalize);
    /** @type {Record<string, number>} */
    const map = {};
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      for (const alias of aliases) {
        const index = normalized.indexOf(alias);
        if (index !== -1) {
          map[field] = index;
          break;
        }
      }
    }
    return map;
  };

  /**
   * Deckbox liefert unter "Edition" den vollen Setnamen. Nur kurze Codes
   * (bis 6 Zeichen, ohne Leerzeichen) werden als Set-Code übernommen.
   * @param {string} value
   */
  const toSetCode = (value) => {
    const v = String(value ?? '').trim();
    if (!v || v.length > 6 || /\s/.test(v)) return '';
    return v.toUpperCase();
  };

  const toFinish = (value) => {
    const v = normalize(value);
    if (!v || ['0', 'false', 'no', 'nein', 'normal', 'nonfoil', 'non-foil', 'regular'].includes(v)) return 'nonfoil';
    if (v.includes('etched')) return 'etched';
    return 'foil';
  };

  class AnkaufForm extends HTMLElement {
    /** @type {AbortController|null} */
    #controller = null;
    #rowCounter = 0;
    /** @type {WeakMap<HTMLLIElement, {mode: string, items: any[], active: number, timer: number, abort: AbortController|null, cardName: string}>} */
    #suggest = new WeakMap();
    /** @type {Map<string, any>} */
    #cache = new Map();
    /** @type {Set<HTMLLIElement>} */
    #imageQueue = new Set();
    #imageTimer = 0;
    #collapsed = false;
    #previewDone = false;
    #previewStale = false;
    /** @type {AbortController|null} */
    #previewController = null;
    /** @type {WeakMap<HTMLElement, IntersectionObserver>} */
    #thumbObservers = new WeakMap();

    connectedCallback() {
      this.strings = JSON.parse(this.querySelector('[data-ankauf-strings]')?.textContent || '{}');
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.maxRows = Number.parseInt(this.dataset.maxRows || '300', 10);
      this.showEstimate = this.dataset.showEstimate === 'true';
      this.autocomplete = this.dataset.autocomplete === 'true';

      this.form = this.querySelector('[data-ankauf-form]');
      this.rowsList = this.querySelector('[data-ankauf-rows]');
      this.template = this.querySelector('[data-ankauf-row-template]');
      this.addButton = this.querySelector('[data-ankauf-add]');
      this.countEl = this.querySelector('[data-ankauf-count]');
      this.statusEl = this.querySelector('[data-ankauf-status]');
      this.errorsEl = this.querySelector('[data-ankauf-errors]');
      this.errorList = this.querySelector('[data-ankauf-error-list]');
      this.submitButton = this.querySelector('[data-ankauf-submit]');
      this.resultEl = this.querySelector('[data-ankauf-result]');
      this.collapseButton = this.querySelector('[data-ankauf-collapse]');
      this.previewEl = this.querySelector('[data-ankauf-preview]');
      this.previewButton = this.querySelector('[data-ankauf-preview-button]');
      this.previewResultEl = this.querySelector('[data-ankauf-preview-result]');
      this.previewValueEl = this.querySelector('[data-ankauf-preview-value]');
      this.previewFactsEl = this.querySelector('[data-ankauf-preview-facts]');
      this.previewStaleEl = this.querySelector('[data-ankauf-preview-stale]');
      this.previewErrorEl = this.querySelector('[data-ankauf-preview-error]');
      this.showRowEstimates = this.dataset.rowEstimates === 'true';
      this.collapsedEl = this.querySelector('[data-ankauf-collapsed]');

      if (!this.form || !this.rowsList || !this.template) return;

      this.addButton?.addEventListener('click', this.#onAddClick);
      this.collapseButton?.addEventListener('click', this.#onCollapseClick);
      this.previewButton?.addEventListener('click', this.#onPreviewClick);
      this.querySelector('[data-ankauf-expand]')?.addEventListener('click', this.#onExpandClick);
      this.rowsList.addEventListener('click', this.#onRowsClick);
      this.rowsList.addEventListener('input', this.#onRowsInput);
      if (this.autocomplete) {
        this.rowsList.addEventListener('keydown', this.#onRowsKeydown);
        this.rowsList.addEventListener('focusout', this.#onRowsFocusout);
        this.rowsList.addEventListener('mousedown', this.#onSuggestMousedown);
        this.rowsList.addEventListener('click', this.#onSuggestClick);
        this.rowsList.addEventListener('focusout', this.#onRowsFocusoutImage);
      }
      this.querySelector('[data-ankauf-csv]')?.addEventListener('change', this.#onCsvChange);
      this.form.addEventListener('submit', this.#onSubmit);
      this.querySelector('[data-ankauf-restart]')?.addEventListener('click', this.#onRestart);

      this.#addRow();
      this.#updateState();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#previewController?.abort();
    }

    /* ---------- Zeilen ---------- */

    /**
     * @param {Partial<Record<'qty'|'name'|'set'|'cn'|'condition'|'lang'|'finish', string|number>>} [data]
     * @returns {HTMLLIElement|null}
     */
    #addRow(data = {}) {
      if (this.#rows().length >= this.maxRows) {
        this.#announce(format(this.strings.rowsLimit, { max: this.maxRows }));
        return null;
      }
      this.#rowCounter += 1;
      const uid = `${this.id || 'ankauf'}-${this.#rowCounter}`;
      const fragment = this.template.content.cloneNode(true);
      const row = /** @type {HTMLLIElement} */ (fragment.querySelector('li'));

      for (const label of row.querySelectorAll('label[data-for]')) {
        const field = label.dataset.for;
        const control = row.querySelector(`[name="${field}"]`);
        if (!control) continue;
        control.id = `AnkaufRow-${uid}-${field}`;
        label.htmlFor = control.id;
      }

      const nameInput = row.querySelector('[name="name"]');
      const list = row.querySelector('[data-ankauf-suggest]');
      if (this.autocomplete && nameInput && list) {
        list.id = `AnkaufSuggest-${uid}`;
        nameInput.setAttribute('role', 'combobox');
        nameInput.setAttribute('aria-autocomplete', 'list');
        nameInput.setAttribute('aria-expanded', 'false');
        nameInput.setAttribute('aria-controls', list.id);
      } else {
        list?.remove();
      }

      const set = (name, value) => {
        if (value === undefined || value === null || value === '') return;
        const control = row.querySelector(`[name="${name}"]`);
        if (control) control.value = String(value);
      };
      set('qty', data.qty);
      set('name', data.name);
      set('set', data.set);
      set('cn', data.cn);
      if (CONDITIONS.includes(String(data.condition))) set('condition', data.condition);
      if (LANGUAGES.includes(String(data.lang))) set('lang', data.lang);
      set('finish', data.finish);

      this.rowsList.append(row);
      return row;
    }

    /** @returns {HTMLLIElement[]} */
    #rows() {
      return Array.from(this.rowsList.querySelectorAll(':scope > li.ankauf-form__row'));
    }

    #isRowEmpty(row) {
      const name = row.querySelector('[name="name"]')?.value.trim();
      const set = row.querySelector('[name="set"]')?.value.trim();
      const cn = row.querySelector('[name="cn"]')?.value.trim();
      return !name && !set && !cn;
    }

    #updateState() {
      const rows = this.#rows();
      let cards = 0;
      rows.forEach((row, index) => {
        const label = format(this.strings.rowLabel, { index: index + 1 });
        row.setAttribute('aria-label', label);
        const remove = row.querySelector('[data-ankauf-remove]');
        if (remove) {
          remove.setAttribute('aria-label', format(this.strings.removeRow, { index: index + 1 }));
          remove.disabled = rows.length === 1;
        }
        if (!this.#isRowEmpty(row)) {
          const qty = Number.parseInt(row.querySelector('[name="qty"]')?.value || '0', 10);
          cards += Number.isFinite(qty) && qty > 0 ? qty : 0;
        }
      });
      if (this.addButton) this.addButton.disabled = rows.length >= this.maxRows;
      this.#markPreviewStale();
      this.#updateCollapse(rows);
      if (this.countEl) {
        this.countEl.textContent = cards > 0 ? format(this.strings.cardCount, { count: cards, rows: rows.length }) : '';
      }
    }

    #onAddClick = () => {
      const row = this.#addRow();
      this.#updateState();
      row?.querySelector('[name="name"]')?.focus();
    };

    #onRowsClick = (event) => {
      const button = event.target.closest('[data-ankauf-remove]');
      if (!button) return;
      const row = button.closest('li.ankauf-form__row');
      const rows = this.#rows();
      if (!row || rows.length <= 1) return;
      const index = rows.indexOf(row);
      row.remove();
      this.#updateState();
      const next = this.#rows()[Math.min(index, this.#rows().length - 1)];
      next?.querySelector('[name="name"]')?.focus();
    };

    #onRowsInput = (event) => {
      const control = event.target;
      if (control.getAttribute('aria-invalid') === 'true') control.removeAttribute('aria-invalid');
      if (control.name === 'set') control.value = control.value.toUpperCase();
      if (this.autocomplete && control.name === 'name' && event.isTrusted !== false) this.#onNameInput(control);
      if (this.autocomplete && (control.name === 'set' || control.name === 'cn')) {
        const row = control.closest('li.ankauf-form__row');
        // Wer Set/Nummer nach einer Auswahl selbst ändert, will eine andere Version.
        if (row && row.dataset.autofilled === 'true') {
          delete row.dataset.autofilled;
          for (const option of row.querySelectorAll('[name="finish"] option')) option.disabled = false;
          // Die übernommene Nummer gehört zum alten Set.
          if (control.name === 'set') row.querySelector('[name="cn"]').value = '';
          // Wurde die Karte über Set + Nummer gefunden, gehört auch der Name zur alten Eingabe.
          if (row.dataset.autofilledBy === 'number') {
            row.querySelector('[name="name"]').value = '';
            this.#setRowImage(row, '');
          }
          delete row.dataset.autofilledBy;
        }
        if (row) this.#onFilterInput(row);
      }
      if (this.autocomplete && ['name', 'set', 'cn'].includes(control.name)) {
        const row = control.closest('li.ankauf-form__row');
        if (row && row.dataset.imageKey && row.dataset.imageKey !== this.#rowKey(row)) this.#setRowImage(row, '');
      }
      this.#updateState();
    };

    /* ---------- Kartenbilder in der Tabelle ---------- */

    /** Schlüssel, unter dem eine Zeile bei Scryfall gesucht wird (wie im Backend). */
    #rowKey(row) {
      const name = normalize(frontName(row.querySelector('[name="name"]')?.value));
      const set = normalize(row.querySelector('[name="set"]')?.value);
      const cn = normalize(row.querySelector('[name="cn"]')?.value);
      if (set && cn) return `sc|${set}|${cn}`;
      if (!name) return '';
      return set ? `ns|${name}|${set}` : `n|${name}`;
    }

    #setRowImage(row, src) {
      const thumb = row.querySelector('[data-ankauf-thumb]');
      if (!thumb) return;
      thumb.replaceChildren();
      thumb.classList.remove('has-image');
      if (!src) {
        delete row.dataset.imageKey;
        return;
      }
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.width = 146;
      img.height = 204;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', () => this.#setRowImage(row, ''), { once: true });
      thumb.append(img);
      thumb.classList.add('has-image');
      row.dataset.imageKey = this.#rowKey(row);
    }

    /** Merkt eine Zeile zum Nachladen vor; mehrere Zeilen werden gebündelt abgefragt. */
    #queueImage(row) {
      if (!this.autocomplete || !row || !row.isConnected) return;
      const key = this.#rowKey(row);
      if (!key || row.dataset.imageKey === key || row.dataset.imageMiss === key) return;
      this.#imageQueue.add(row);
      window.clearTimeout(this.#imageTimer);
      this.#imageTimer = window.setTimeout(() => this.#flushImages(), IMAGE_DELAY_MS);
    }

    async #flushImages() {
      const rows = Array.from(this.#imageQueue).filter((row) => row.isConnected);
      this.#imageQueue.clear();

      /** @type {Map<string, {identifier: object, rows: HTMLLIElement[]}>} */
      const wanted = new Map();
      for (const row of rows) {
        const key = this.#rowKey(row);
        if (!key) continue;
        if (this.#cache.has(`img:${key}`)) {
          const src = this.#cache.get(`img:${key}`);
          if (src) this.#setRowImage(row, src);
          else row.dataset.imageMiss = key;
          continue;
        }
        if (!wanted.has(key)) {
          const name = frontName(row.querySelector('[name="name"]').value);
          const set = normalize(row.querySelector('[name="set"]').value);
          const cn = row.querySelector('[name="cn"]').value.trim();
          let identifier = { name };
          if (set && cn) identifier = { set, collector_number: cn };
          else if (set) identifier = { name, set };
          wanted.set(key, { identifier, rows: [] });
        }
        wanted.get(key).rows.push(row);
      }

      const keys = Array.from(wanted.keys());
      for (let start = 0; start < keys.length; start += COLLECTION_BATCH) {
        const batch = keys.slice(start, start + COLLECTION_BATCH);
        try {
          const response = await fetch(`${SCRYFALL_API}/cards/collection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ identifiers: batch.map((k) => wanted.get(k).identifier) })
          });
          if (!response.ok) break;
          const data = await response.json();
          const found = new Map();
          for (const card of data.data || []) {
            const src = smallImage(card);
            const set = normalize(card.set);
            found.set(`sc|${set}|${normalize(card.collector_number)}`, src);
            const names = [card.name, ...(Array.isArray(card.card_faces) ? card.card_faces.map((f) => f.name) : [])];
            for (const n of names) {
              const name = normalize(frontName(n));
              if (!found.has(`ns|${name}|${set}`)) found.set(`ns|${name}|${set}`, src);
              if (!found.has(`n|${name}`)) found.set(`n|${name}`, src);
            }
          }
          for (const key of batch) {
            const src = found.get(key) || '';
            this.#cache.set(`img:${key}`, src);
            for (const row of wanted.get(key).rows) {
              if (!row.isConnected || this.#rowKey(row) !== key) continue;
              if (src) this.#setRowImage(row, src);
              else row.dataset.imageMiss = key;
            }
          }
        } catch (error) {
          break; // Bilder sind optional.
        }
        if (start + COLLECTION_BATCH < keys.length) await new Promise((resolve) => setTimeout(resolve, 120));
      }

      // Nicht gefunden und ohne Nummer? Vielleicht ein deutscher Kartenname (max. 8 pro Durchgang).
      const germanKeys = keys.filter((key) => !key.startsWith('sc|') && this.#cache.get(`img:${key}`) === '').slice(0, 8);
      for (const key of germanKeys) {
        const { identifier, rows: keyRows } = wanted.get(key);
        const q = `!"${identifier.name.replace(/"/g, '')}" lang:de${identifier.set ? ` set:${identifier.set}` : ''}`;
        try {
          const response = await fetch(`${SCRYFALL_API}/cards/search?q=${encodeURIComponent(q)}&unique=cards`, {
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) continue;
          const data = await response.json();
          const src = smallImage(data && data.data && data.data[0]);
          if (!src) continue;
          this.#cache.set(`img:${key}`, src);
          for (const row of keyRows) {
            if (!row.isConnected || this.#rowKey(row) !== key) continue;
            delete row.dataset.imageMiss;
            this.#setRowImage(row, src);
          }
        } catch (error) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
    }

    #onRowsFocusoutImage = (event) => {
      const control = event.target;
      if (!['name', 'set', 'cn'].includes(control.name)) return;
      const row = control.closest('li.ankauf-form__row');
      window.setTimeout(() => {
        const state = this.#suggest.get(row);
        if (state && (state.mode === 'names' || state.mode === 'prints') && row.contains(document.activeElement)) return;
        this.#queueImage(row);
      }, 150);
    };

    /* ---------- Scryfall-Vorschläge (Combobox) ---------- */

    #state(row) {
      let state = this.#suggest.get(row);
      if (!state) {
        state = { mode: 'closed', items: [], active: -1, timer: 0, abort: null, cardName: '' };
        this.#suggest.set(row, state);
      }
      return state;
    }

    /** Tippt jemand nach einer Auswahl weiter, passen Set/Nummer nicht mehr – zurücksetzen. */
    #clearAutofill(row) {
      if (row.dataset.autofilled !== 'true') return;
      delete row.dataset.autofilled;
      delete row.dataset.autofilledBy;
      const setInput = row.querySelector('[name="set"]');
      const cnInput = row.querySelector('[name="cn"]');
      if (setInput) setInput.value = '';
      if (cnInput) cnInput.value = '';
      for (const option of row.querySelectorAll('[name="finish"] option')) option.disabled = false;
      this.#setRowImage(row, '');
    }

    #onNameInput(input) {
      const row = input.closest('li.ankauf-form__row');
      const state = this.#state(row);
      this.#clearAutofill(row);
      window.clearTimeout(state.timer);
      const query = input.value.trim();
      if (query.length < SUGGEST_MIN_CHARS) {
        this.#closeSuggest(row);
        return;
      }
      const filter = this.#rowFilter(row);
      state.timer = window.setTimeout(
        () => (filter ? this.#loadFiltered(row, query, filter) : this.#loadNames(row, query)),
        SUGGEST_DELAY_MS
      );
    }

    /**
     * Set-Code oder Nummer wurde geändert: Steht schon ein Name in der Zeile
     * (oder sind Set + Nummer komplett), passende Versionen vorschlagen.
     */
    #onFilterInput(row) {
      const state = this.#state(row);
      window.clearTimeout(state.timer);
      const name = (row.querySelector('[name="name"]')?.value || '').trim();
      const filter = this.#rowFilter(row);
      if (!filter) {
        this.#closeSuggest(row);
        return;
      }
      // Set-Codes haben mindestens 3 Zeichen – vorher nicht suchen, sonst gäbe es beim Tippen Fehlermeldungen.
      const setOk = filter.set.length === 0 || filter.set.length >= 3;
      const hasFilter = filter.set.length >= 3 || filter.cn.length > 0;
      const hasName = name.length >= SUGGEST_MIN_CHARS;
      const setAndNumber = filter.set.length >= 3 && filter.cn.length > 0;
      if (!setOk || !hasFilter || !(hasName || setAndNumber)) return;
      state.timer = window.setTimeout(() => this.#loadFiltered(row, name, filter), FILTER_DELAY_MS);
    }

    /**
     * Sucht Karten über ihren deutschen Namen (auch Teilwörter).
     * Liefert nur Treffer, bei denen der deutsche Name passt – nicht der englische.
     * @returns {Promise<{name: string, german: string}[]>}
     */
    async #germanMatches(query, state) {
      const words = frontName(query).replace(/["():<>=!\/]/g, ' ').replace(/\s+/g, ' ').trim();
      if (words.length < 3) return [];
      const data = await this.#fetchJson(
        `${SCRYFALL_API}/cards/search?q=${encodeURIComponent(`${words} lang:de`)}&unique=cards&order=edhrec`,
        state,
        true
      );
      const needles = normalize(words).split(' ');
      const matches = [];
      const seen = new Set();
      for (const card of (data && data.data) || []) {
        const printed = card.printed_name || (Array.isArray(card.card_faces) ? card.card_faces.map((f) => f.printed_name).filter(Boolean).join(' // ') : '');
        if (!printed || seen.has(card.name)) continue;
        const german = normalize(printed);
        const english = normalize(card.name);
        if (german === english || needles.every((n) => english.includes(n))) continue;
        if (!needles.every((n) => german.includes(n))) continue;
        seen.add(card.name);
        matches.push({ name: card.name, german: printed, starts: german.startsWith(needles[0]) });
      }
      matches.sort((a, b) => Number(b.starts) - Number(a.starts));
      return matches.slice(0, 20);
    }

    /** Selbst eingetragene Set-Codes/Nummern schränken die Suche ein. */
    #rowFilter(row) {
      if (row.dataset.autofilled === 'true') return null;
      const set = (row.querySelector('[name="set"]')?.value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      const cn = (row.querySelector('[name="cn"]')?.value || '').trim().replace(/["\\]/g, '');
      if (!set && !cn) return null;
      return { set, cn };
    }

    #filterLabel(filter) {
      return [filter.set ? `Set ${filter.set.toUpperCase()}` : '', filter.cn ? `Nr. ${filter.cn}` : ''].filter(Boolean).join(' · ');
    }

    #mapPrint(card) {
      return {
        kind: 'print',
        name: card.name,
        set: String(card.set || '').toUpperCase(),
        setName: card.set_name || '',
        cn: card.collector_number || '',
        year: String(card.released_at || '').slice(0, 4),
        finishes: Array.isArray(card.finishes) ? card.finishes : ['nonfoil'],
        image: smallImage(card)
      };
    }

    /** Teilname + Set und/oder Nummer: direkt passende Versionen zeigen. */
    async #loadFiltered(row, query, filter) {
      const state = this.#state(row);
      this.#renderMessage(row, this.strings.acLoading);
      const words = frontName(query).replace(/["():<>=!\/]/g, ' ').replace(/\s+/g, ' ').trim();
      const parts = words ? [words] : [];
      if (filter.set) parts.push(`set:${filter.set}`);
      if (filter.cn) parts.push(`cn:"${filter.cn}"`);
      parts.push('game:paper');
      try {
        const data = await this.#fetchJson(
          `${SCRYFALL_API}/cards/search?q=${encodeURIComponent(parts.join(' '))}&unique=prints&order=released&dir=desc`,
          state
        );
        const input = row.querySelector('[name="name"]');
        if (!input || input.value.trim() !== query) return;
        const current = this.#rowFilter(row);
        if (!current || current.set !== filter.set || current.cn !== filter.cn) return;
        const label = this.#filterLabel(filter);
        let prints = ((data && data.data) || []).map((card) => this.#mapPrint(card));
        state.germanMap = new Map();

        // Nichts gefunden? Vielleicht ist es ein deutscher Kartenname.
        if (prints.length === 0 && words.length >= 3) {
          const german = await this.#germanMatches(query, state);
          if (german.length > 0) {
            for (const match of german) state.germanMap.set(match.name, match.german);
            const byName = german.map((m) => `!"${m.name.replace(/"/g, '')}"`).join(' or ');
            const rest = parts.filter((part) => part !== words);
            const germanData = await this.#fetchJson(
              `${SCRYFALL_API}/cards/search?q=${encodeURIComponent(`(${byName}) ${rest.join(' ')}`)}&unique=prints&order=released&dir=desc`,
              state
            );
            if (input.value.trim() !== query) return;
            prints = ((germanData && germanData.data) || []).map((card) => this.#mapPrint(card));
          }
        }

        if (prints.length === 0) {
          this.#renderMessage(row, format(this.strings.acNoResultsFiltered, { filter: label }));
          return;
        }

        // Set + Nummer ohne Namen und genau ein Treffer: Zeile direkt ausfüllen, Fokus bleibt im Feld.
        if (!query && filter.set && filter.cn && prints.length === 1) {
          const active = document.activeElement;
          state.mode = 'prints';
          state.items = prints;
          this.#choose(row, 0, { keepFocus: true });
          row.dataset.autofilledBy = 'number';
          if (active && row.contains(active)) active.focus();
          return;
        }

        state.mode = 'prints';
        state.items = prints;
        this.#renderOptions(row, format(this.strings.acFilteredResults, { count: prints.length, filter: label }), format(this.strings.acFilterNote, { filter: label }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.#renderMessage(row, this.strings.acError);
      }
    }

    /**
     * @param {boolean} [parallel] true = laufende Anfragen nicht abbrechen (für gleichzeitige Abrufe).
     */
    async #fetchJson(url, state, parallel = false) {
      if (this.#cache.has(url)) return this.#cache.get(url);
      if (!parallel || !state.abort) {
        state.abort?.abort();
        state.abort = new AbortController();
      }
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: state.abort.signal });
      if (response.status === 404) {
        this.#cache.set(url, null);
        return null;
      }
      if (!response.ok) throw new Error(`Scryfall ${response.status}`);
      const data = await response.json();
      this.#cache.set(url, data);
      return data;
    }

    async #loadNames(row, query) {
      const state = this.#state(row);
      this.#renderMessage(row, this.strings.acLoading);
      try {
        state.abort?.abort();
        state.abort = new AbortController();
        const [data, german] = await Promise.all([
          this.#fetchJson(`${SCRYFALL_API}/cards/autocomplete?q=${encodeURIComponent(query)}`, state, true),
          this.#germanMatches(query, state)
        ]);
        const input = row.querySelector('[name="name"]');
        if (!input || input.value.trim() !== query) return;

        // Englische Treffer zuerst, danach Karten, die über ihren deutschen Namen gefunden wurden.
        const names = [...((data && data.data) || [])];
        state.germanMap = new Map();
        for (const match of german) {
          state.germanMap.set(match.name, match.german);
          if (!names.includes(match.name)) names.push(match.name);
        }
        if (names.length === 0) {
          this.#renderMessage(row, this.strings.acNoResults);
          return;
        }
        await this.#loadAllPrints(row, query, names.slice(0, 20));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.#renderMessage(row, this.strings.acError);
      }
    }

    /**
     * Alle Versionen aller passenden Karten auf einmal laden, gruppiert nach Karte.
     * Gibt es keine Papierversionen, bleibt es bei der reinen Namensliste.
     */
    async #loadAllPrints(row, query, names) {
      const state = this.#state(row);
      const q = `(${names.map((n) => `!"${n.replace(/"/g, '')}"`).join(' or ')}) game:paper`;
      const data = await this.#fetchJson(
        `${SCRYFALL_API}/cards/search?q=${encodeURIComponent(q)}&unique=prints&order=released&dir=desc`,
        state
      );
      const input = row.querySelector('[name="name"]');
      if (!input || input.value.trim() !== query || this.#rowFilter(row)) return;

      const prints = ((data && data.data) || []).map((card) => this.#mapPrint(card));
      if (prints.length === 0) {
        state.mode = 'names';
        state.items = names.map((name) => ({ kind: 'name', name, image: '' }));
        this.#renderOptions(row, format(this.strings.acResults, { count: names.length }));
        this.#loadNameImages(row, state.items);
        return;
      }
      state.mode = 'prints';
      state.groupNames = names;
      state.allPrints = prints;
      state.nextPage = data && data.has_more ? data.next_page : '';
      state.totalPrints = (data && data.total_cards) || prints.length;
      state.items = this.#groupItems(state);
      this.#renderOptions(
        row,
        format(this.strings.acAllResults, { count: state.totalPrints, cards: this.#groupCount(state) }),
        this.strings.acAllNote
      );
    }

    #groupCount(state) {
      return state.items.filter((item) => item.kind === 'group').length;
    }

    /** Baut die Liste: je Karte eine Kopfzeile („Version weiß ich nicht“), darunter ihre Versionen. */
    #groupItems(state) {
      const groups = new Map();
      for (const name of state.groupNames) groups.set(name, []);
      for (const print of state.allPrints) {
        if (!groups.has(print.name)) groups.set(print.name, []);
        groups.get(print.name).push(print);
      }
      const items = [];
      for (const [name, prints] of groups) {
        if (prints.length === 0) continue;
        items.push({ kind: 'group', name, german: state.germanMap?.get(name) || '', image: prints[0].image, count: prints.length });
        items.push(...prints);
      }
      if (state.nextPage) items.push({ kind: 'more' });
      return items;
    }

    async #loadMore(row) {
      const state = this.#state(row);
      if (!state.nextPage) return;
      const list = row.querySelector('[data-ankauf-suggest]');
      const scroll = list ? list.scrollTop : 0;
      const firstNew = state.items.length - 1;
      try {
        const data = await this.#fetchJson(state.nextPage, state);
        state.allPrints.push(...((data && data.data) || []).map((card) => this.#mapPrint(card)));
        state.nextPage = data && data.has_more ? data.next_page : '';
        state.items = this.#groupItems(state);
        this.#renderOptions(row, format(this.strings.acAllResults, { count: state.totalPrints, cards: this.#groupCount(state) }), this.strings.acAllNote);
        if (list) list.scrollTop = scroll;
        this.#setActive(row, Math.min(firstNew, state.items.length - 1));
      } catch (error) {
        if (error.name !== 'AbortError') this.#announce(this.strings.acError);
      }
    }

    async #loadPrints(row, name) {
      const state = this.#state(row);
      state.cardName = name;
      this.#renderMessage(row, this.strings.acLoading);
      try {
        const q = `!"${name.replace(/"/g, '')}" game:paper`;
        const data = await this.#fetchJson(
          `${SCRYFALL_API}/cards/search?q=${encodeURIComponent(q)}&unique=prints&order=released&dir=desc`,
          state
        );
        const prints = ((data && data.data) || []).map((card) => this.#mapPrint(card));
        state.mode = 'prints';
        state.items = [{ kind: 'unknown', name }, ...prints];
        this.#renderOptions(row, format(this.strings.acPrints, { count: prints.length, name }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.#renderMessage(row, this.strings.acError);
      }
    }

    /**
     * Holt die Bilder zu den Namensvorschlägen mit einer einzigen Sammelabfrage
     * und setzt sie nachträglich ein. Schlägt das fehl, bleibt die Liste ohne Bilder.
     */
    async #loadNameImages(row, items) {
      const key = `collection:${items.map((i) => i.name).join('|')}`;
      try {
        let images = this.#cache.get(key);
        if (!images) {
          const response = await fetch(`${SCRYFALL_API}/cards/collection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ identifiers: items.map((i) => ({ name: frontName(i.name) })) })
          });
          if (!response.ok) return;
          const data = await response.json();
          images = {};
          for (const card of data.data || []) images[normalize(frontName(card.name))] = smallImage(card);
          this.#cache.set(key, images);
        }
        const state = this.#suggest.get(row);
        if (!state || state.mode !== 'names' || state.items !== items) return;
        items.forEach((item, index) => {
          item.image = images[normalize(frontName(item.name))] || '';
          const option = row.querySelector(`[data-ankauf-suggest] [data-index="${index}"]`);
          if (option && item.image) this.#setThumb(option, item.image);
        });
      } catch (error) {
        // Bilder sind optional – die Vorschläge funktionieren auch ohne.
      }
    }

    /**
     * @param {HTMLElement} option
     * @param {boolean} [lazy] Bild erst laden, wenn es in der Liste sichtbar wird.
     */
    #setThumb(option, src, lazy = false) {
      const thumb = option.querySelector('.ankauf-form__suggest-thumb');
      if (!thumb || thumb.querySelector('img')) return;
      const img = document.createElement('img');
      if (lazy) img.dataset.src = src;
      else img.src = src;
      img.alt = '';
      img.width = 146;
      img.height = 204;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', () => img.remove(), { once: true });
      thumb.append(img);
    }

    /** Lädt Bilder erst, wenn sie im aufgeklappten Dropdown sichtbar werden. */
    #observeThumbs(list) {
      let observer = this.#thumbObservers.get(list);
      if (!observer) {
        observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const img = entry.target;
              img.src = img.dataset.src;
              delete img.dataset.src;
              observer.unobserve(img);
            }
          },
          { root: list, rootMargin: '200px 0px' }
        );
        this.#thumbObservers.set(list, observer);
      }
      observer.disconnect();
      for (const img of list.querySelectorAll('img[data-src]')) observer.observe(img);
    }

    /** @param {HTMLLIElement} row */
    #renderMessage(row, message) {
      const list = row.querySelector('[data-ankauf-suggest]');
      const input = row.querySelector('[name="name"]');
      if (!list || !input) return;
      const state = this.#state(row);
      state.mode = 'message';
      state.items = [];
      state.active = -1;
      const item = document.createElement('li');
      item.className = 'ankauf-form__suggest-message';
      item.setAttribute('role', 'presentation');
      item.textContent = message;
      list.replaceChildren(item);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    #finishLabel(finishes) {
      const hasNonfoil = finishes.includes('nonfoil');
      const hasFoil = finishes.includes('foil');
      const hasEtched = finishes.includes('etched');
      if (hasNonfoil && (hasFoil || hasEtched)) return this.strings.acFinishBoth;
      if (hasEtched && !hasFoil && !hasNonfoil) return this.strings.acFinishEtched;
      if (!hasNonfoil) return this.strings.acFinishFoil;
      return this.strings.acFinishNonfoil;
    }

    #renderOptions(row, announcement, note = '') {
      const list = row.querySelector('[data-ankauf-suggest]');
      const input = row.querySelector('[name="name"]');
      if (!list || !input) return;
      const state = this.#state(row);
      state.active = -1;
      const fragment = document.createDocumentFragment();
      state.items.forEach((item, index) => {
        const option = document.createElement('li');
        option.id = `${list.id}-opt-${index}`;
        option.className = 'ankauf-form__suggest-option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.index = String(index);

        const thumb = document.createElement('span');
        thumb.className = 'ankauf-form__suggest-thumb';
        thumb.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span');
        text.className = 'ankauf-form__suggest-text';
        option.append(thumb, text);

        if (item.kind === 'name') {
          text.textContent = item.name;
        } else if (item.kind === 'group') {
          option.classList.add('ankauf-form__suggest-option--group');
          thumb.classList.add('ankauf-form__suggest-thumb--empty');
          const title = document.createElement('span');
          title.className = 'ankauf-form__suggest-title';
          title.textContent = item.name;
          const meta = document.createElement('span');
          meta.className = 'ankauf-form__suggest-meta';
          const hint = format(item.count === 1 ? this.strings.acGroupHintOne : this.strings.acGroupHint, { count: item.count });
          meta.textContent = item.german ? `${item.german} · ${hint}` : hint;
          text.append(title, meta);
        } else if (item.kind === 'more') {
          option.classList.add('ankauf-form__suggest-option--more');
          thumb.classList.add('ankauf-form__suggest-thumb--empty');
          text.textContent = this.strings.acLoadMore;
        } else if (item.kind === 'unknown') {
          option.classList.add('ankauf-form__suggest-option--muted');
          thumb.classList.add('ankauf-form__suggest-thumb--empty');
          text.textContent = this.strings.acUnknownVersion;
        } else {
          const title = document.createElement('span');
          title.className = 'ankauf-form__suggest-title';
          title.textContent = item.setName;
          const meta = document.createElement('span');
          meta.className = 'ankauf-form__suggest-meta';
          meta.textContent = [item.set, `#${item.cn}`, item.year, this.#finishLabel(item.finishes)].filter(Boolean).join(' · ');
          const germanName = state.mode === 'prints' && this.#suggest.get(row)?.germanMap?.get(item.name);
          if (germanName && !state.items.some((i) => i.kind === 'group')) title.textContent = `${item.setName} – ${germanName}`;
          text.append(title, meta);
        }
        if (item.image && item.kind !== 'group') this.#setThumb(option, item.image, true);
        fragment.append(option);
      });
      if (note) {
        const noteItem = document.createElement('li');
        noteItem.className = 'ankauf-form__suggest-note';
        noteItem.setAttribute('role', 'presentation');
        noteItem.textContent = note;
        fragment.prepend(noteItem);
      }
      list.replaceChildren(fragment);
      list.scrollTop = 0;
      this.#observeThumbs(list);
      list.hidden = false;
      // Einmal pro Öffnen sichtbar scrollen, nicht bei jedem weiteren Tastendruck.
      if (!state.revealed) {
        state.revealed = true;
        this.#revealList(input, list);
      }
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      this.#announce(announcement);
    }

    /** Ragt die Liste unten aus dem Bildschirm (v. a. am Handy), Seite so scrollen, dass sie sichtbar wird. */
    #revealList(input, list) {
      const overflow = list.getBoundingClientRect().bottom - window.innerHeight;
      if (overflow <= 0) return;
      const room = input.getBoundingClientRect().top - 16;
      const distance = Math.min(overflow + 16, room);
      if (distance <= 0) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: distance, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    #closeSuggest(row) {
      const state = this.#suggest.get(row);
      if (state) {
        window.clearTimeout(state.timer);
        state.abort?.abort();
        state.mode = 'closed';
        state.items = [];
        state.active = -1;
        state.revealed = false;
      }
      const list = row.querySelector('[data-ankauf-suggest]');
      const input = row.querySelector('[name="name"]');
      if (list) {
        list.hidden = true;
        list.replaceChildren();
      }
      input?.setAttribute('aria-expanded', 'false');
      input?.removeAttribute('aria-activedescendant');
    }

    #setActive(row, index) {
      const state = this.#state(row);
      const list = row.querySelector('[data-ankauf-suggest]');
      const input = row.querySelector('[name="name"]');
      const options = list ? Array.from(list.querySelectorAll('[role="option"]')) : [];
      if (options.length === 0) return;
      const next = (index + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === next)));
      state.active = next;
      const active = options.at(next);
      input?.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
      if (document.activeElement !== input) this.#announce(active.textContent);
    }

    #choose(row, index, options = {}) {
      const state = this.#state(row);
      const item = state.items.at(index);
      const input = row.querySelector('[name="name"]');
      if (!item || !input) return;

      if (item.kind === 'name') {
        input.value = item.name;
        this.#clearAutofill(row);
        state.nameImage = item.image || '';
        this.#loadPrints(row, item.name);
        return;
      }

      if (item.kind === 'more') {
        this.#loadMore(row);
        return;
      }

      if (item.kind === 'group') state.nameImage = item.image || '';

      input.value = item.name;
      this.#closeSuggest(row);

      if (item.kind === 'print') {
        row.querySelector('[name="set"]').value = item.set;
        row.querySelector('[name="cn"]').value = item.cn;
        const finish = row.querySelector('[name="finish"]');
        if (finish) {
          for (const option of finish.options) option.disabled = !item.finishes.includes(option.value);
          if (!item.finishes.includes(finish.value)) {
            finish.value = item.finishes.includes('nonfoil') ? 'nonfoil' : item.finishes[0];
          }
        }
        row.dataset.autofilled = 'true';
        this.#setRowImage(row, item.image);
        this.#announce(format(this.strings.acFilled, { name: item.name, set: item.set, number: item.cn }));
      } else {
        this.#clearAutofill(row);
        if (state.nameImage) this.#setRowImage(row, state.nameImage);
        else this.#queueImage(row);
      }
      input.removeAttribute('aria-invalid');
      // Über den deutschen Namen gefunden: Sprache auf Deutsch stellen (nur wenn noch Standard).
      if (state.germanMap?.has(item.name)) {
        const lang = row.querySelector('[name="lang"]');
        if (lang && lang.value === 'en') {
          lang.value = 'de';
          this.#announce(this.strings.acLanguageSet);
        }
      }
      this.#updateState();
      if (!options.keepFocus) row.querySelector('[name="condition"]')?.focus();
    }

    #onRowsKeydown = (event) => {
      const input = event.target;
      if (!['name', 'set', 'cn'].includes(input.name)) return;
      const row = input.closest('li.ankauf-form__row');
      const state = this.#suggest.get(row);
      const open = state && (state.mode === 'names' || state.mode === 'prints');

      switch (event.key) {
        case 'ArrowDown':
          if (!open) return;
          event.preventDefault();
          this.#setActive(row, state.active + 1);
          break;
        case 'ArrowUp':
          if (!open) return;
          event.preventDefault();
          this.#setActive(row, state.active < 0 ? -1 : state.active - 1);
          break;
        case 'Enter':
          if (!open || state.active < 0) return;
          event.preventDefault();
          this.#choose(row, state.active);
          break;
        case 'Escape':
          if (!state || state.mode === 'closed') return;
          event.preventDefault();
          this.#closeSuggest(row);
          break;
        default:
      }
    };

    /** Verhindert, dass ein Klick in die Liste dem Eingabefeld den Fokus nimmt. */
    #onSuggestMousedown = (event) => {
      if (event.target.closest('[data-ankauf-suggest]')) event.preventDefault();
    };

    #onSuggestClick = (event) => {
      const option = event.target.closest('[data-ankauf-suggest] [role="option"]');
      if (!option) return;
      const row = option.closest('li.ankauf-form__row');
      this.#choose(row, Number.parseInt(option.dataset.index, 10));
    };

    #onRowsFocusout = (event) => {
      if (!['name', 'set', 'cn'].includes(event.target.name)) return;
      const row = event.target.closest('li.ankauf-form__row');
      window.setTimeout(() => {
        const active = document.activeElement;
        const inSearchFields = row.contains(active) && ['name', 'set', 'cn'].includes(active.name);
        if (!inSearchFields) this.#closeSuggest(row);
      }, 120);
    };

    /* ---------- Liste einklappen ---------- */

    #updateCollapse(rows = this.#rows()) {
      const collapsible = rows.length > COLLAPSE_THRESHOLD;
      if (!collapsible) this.#collapsed = false;
      const hiddenCount = this.#collapsed ? rows.length - COLLAPSED_VISIBLE : 0;
      rows.forEach((row, index) => {
        row.hidden = index < hiddenCount;
      });
      if (this.collapseButton) {
        this.collapseButton.hidden = !collapsible;
        this.collapseButton.setAttribute('aria-expanded', String(!this.#collapsed));
        const label = this.collapseButton.querySelector('.button-text');
        if (label) {
          label.textContent = this.#collapsed ? format(this.strings.expandList, { rows: rows.length }) : this.strings.collapseList;
        }
      }
      if (this.collapsedEl) {
        this.collapsedEl.hidden = !this.#collapsed;
        const text = this.collapsedEl.querySelector('[data-ankauf-collapsed-text]');
        if (text) text.textContent = format(this.strings.collapsedNote, { count: hiddenCount });
      }
    }

    #onCollapseClick = () => {
      this.#collapsed = !this.#collapsed;
      this.#updateCollapse();
      if (this.#collapsed) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.querySelector('.ankauf-form__table')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    };

    #onExpandClick = () => {
      this.#collapsed = false;
      this.#updateCollapse();
      this.#rows()[0]?.querySelector('[name="name"]')?.focus();
    };

    /* ---------- CSV ---------- */

    #onCsvChange = async (event) => {
      const input = event.target;
      const file = input.files?.[0];
      if (!file) return;

      if (file.size > MAX_CSV_BYTES) {
        this.#announce(this.strings.csvSize);
        input.value = '';
        return;
      }

      let text = '';
      try {
        text = await file.text();
      } catch (error) {
        this.#announce(this.strings.csvRead);
        input.value = '';
        return;
      }

      const rows = parseCsv(text);
      if (rows.length < 2) {
        this.#announce(this.strings.csvEmpty);
        input.value = '';
        return;
      }

      const map = mapHeader(rows[0]);
      if (map.name === undefined) {
        this.#announce(this.strings.csvEmpty);
        input.value = '';
        return;
      }

      // Leere Zeilen werden durch den Import ersetzt.
      for (const row of this.#rows()) {
        if (this.#isRowEmpty(row)) row.remove();
      }

      let added = 0;
      let skipped = 0;
      const cell = (row, field) => (map[field] === undefined ? '' : String(row[map[field]] ?? '').trim());

      for (const row of rows.slice(1)) {
        const name = cell(row, 'name');
        if (!name) {
          skipped += 1;
          continue;
        }
        const qtyRaw = Number.parseInt(cell(row, 'qty') || '1', 10);
        const created = this.#addRow({
          qty: Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.min(qtyRaw, 999) : 1,
          name: name.slice(0, 150),
          set: toSetCode(cell(row, 'set')),
          cn: cell(row, 'cn').slice(0, 10),
          condition: CONDITION_MAP[normalize(cell(row, 'condition'))] || 'NM',
          lang: LANGUAGE_MAP[normalize(cell(row, 'lang'))] || 'en',
          finish: toFinish(cell(row, 'finish'))
        });
        if (created) {
          added += 1;
        } else {
          skipped += rows.length - 1 - added - skipped;
          break;
        }
      }

      if (this.#rows().length === 0) this.#addRow();
      this.#updateState();
      for (const row of this.#rows()) this.#queueImage(row);
      input.value = '';
      this.#announce(format(this.strings.csvSuccess, { count: added, skipped }));
    };

    /* ---------- Validierung & Versand ---------- */

    /** @returns {{ok: boolean, payload?: object}} */
    #validate() {
      const errors = [];
      const fd = new FormData(this.form);
      const nameInput = this.form.elements.namedItem('contact_name');
      const emailInput = this.form.elements.namedItem('contact_email');
      const consentInput = this.form.elements.namedItem('consent');

      for (const el of this.form.querySelectorAll('[aria-invalid="true"]')) el.removeAttribute('aria-invalid');

      const name = String(fd.get('contact_name') || '').trim();
      const email = String(fd.get('contact_email') || '').trim();
      if (!name) errors.push({ message: this.strings.errorName, field: nameInput });
      if (!EMAIL_PATTERN.test(email)) errors.push({ message: this.strings.errorEmail, field: emailInput });

      const { cards } = this.#collectCards(errors);

      if (!consentInput?.checked) errors.push({ message: this.strings.errorConsent, field: consentInput });

      if (errors.length > 0) {
        this.#showErrors(errors);
        return { ok: false };
      }

      this.#hideErrors();
      return {
        ok: true,
        payload: {
          version: 1,
          shop: this.dataset.shop || '',
          page: window.location.href,
          contact: {
            name,
            email,
            phone: String(fd.get('contact_phone') || '').trim(),
            handover: String(fd.get('handover') || 'versand'),
            note: String(fd.get('contact_note') || '').trim()
          },
          consent: true,
          website: String(fd.get('website') || ''),
          cards
        }
      };
    }

    /**
     * Liest alle ausgefüllten Kartenzeilen und sammelt Fehler.
     * @returns {{cards: object[], rows: HTMLLIElement[]}}
     */
    #collectCards(errors) {
      const cards = [];
      const rows = [];
      this.#rows().forEach((row, index) => {
        if (this.#isRowEmpty(row)) return;
        const get = (field) => row.querySelector(`[name="${field}"]`);
        const cardName = get('name').value.trim();
        const qty = Number.parseInt(get('qty').value, 10);
        if (!cardName) errors.push({ message: format(this.strings.errorCardName, { index: index + 1 }), field: get('name') });
        if (!Number.isFinite(qty) || qty < 1) errors.push({ message: format(this.strings.errorCardQty, { index: index + 1 }), field: get('qty') });
        cards.push({
          qty: Number.isFinite(qty) && qty > 0 ? Math.min(qty, 999) : 1,
          name: cardName,
          set: get('set').value.trim().toLowerCase(),
          cn: get('cn').value.trim(),
          condition: get('condition').value,
          lang: get('lang').value,
          finish: get('finish').value
        });
        rows.push(row);
      });
      if (cards.length === 0) errors.push({ message: this.strings.errorNoCards, field: this.#rows()[0]?.querySelector('[name="name"]') });
      return { cards, rows };
    }

    /* ---------- Preisvorschau vor dem Absenden ---------- */

    #money(value) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
    }

    #onPreviewClick = async () => {
      for (const el of this.form.querySelectorAll('[aria-invalid="true"]')) el.removeAttribute('aria-invalid');
      const errors = [];
      const { cards, rows } = this.#collectCards(errors);
      if (errors.length > 0) {
        this.#showErrors(errors);
        return;
      }
      this.#hideErrors();
      if (!this.endpoint) {
        this.#showPreviewError(this.strings.errorConfig);
        return;
      }

      this.#previewController?.abort();
      this.#previewController = new AbortController();
      this.#setPreviewBusy(true);
      this.previewErrorEl.hidden = true;

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({
            mode: 'estimate',
            website: String(new FormData(this.form).get('website') || ''),
            cards
          }),
          redirect: 'follow',
          signal: this.#previewController.signal
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!data || data.ok !== true || !data.estimate) {
          this.#showPreviewError(data && data.error === 'rate_limited' ? this.strings.errorRate : this.strings.previewError);
          return;
        }
        this.#renderPreview(data, rows);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Ankauf: Vorschau fehlgeschlagen', error);
        this.#showPreviewError(this.strings.previewError);
      } finally {
        this.#setPreviewBusy(false);
      }
    };

    #setPreviewBusy(busy) {
      const button = this.previewButton;
      if (!button) return;
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
      const label = button.querySelector('.button-text');
      if (label) label.textContent = busy ? this.strings.previewLoading : this.#previewDone ? this.strings.previewRecalc : this.strings.previewButton;
      if (busy) this.#announce(this.strings.previewLoading);
    }

    #showPreviewError(message) {
      this.previewErrorEl.textContent = message;
      this.previewErrorEl.hidden = false;
    }

    #renderPreview(data, rows) {
      const estimate = data.estimate;
      this.previewValueEl.textContent = this.#money(estimate.total);
      this.previewFactsEl.replaceChildren();
      const addFact = (text) => {
        const li = document.createElement('li');
        li.textContent = text;
        this.previewFactsEl.append(li);
      };
      addFact(format(this.strings.estimateMatched, { matched: estimate.matched, total: estimate.positions }));
      if (estimate.manual > 0) addFact(format(estimate.manual === 1 ? this.strings.estimateManualOne : this.strings.estimateManual, { count: estimate.manual }));
      if (estimate.belowMin > 0) addFact(format(estimate.belowMin === 1 ? this.strings.estimateBelowMinOne : this.strings.estimateBelowMin, { count: estimate.belowMin }));

      this.#clearRowEstimates();
      if (this.showRowEstimates && Array.isArray(data.items)) {
        data.items.forEach((item, index) => {
          const row = rows[index];
          const badge = row && row.querySelector('[data-ankauf-row-estimate]');
          if (!badge) return;
          const qty = Number.parseInt(row.querySelector('[name="qty"]').value, 10) || 1;
          let text = this.strings.rowManual;
          if (item.status === 'ok') {
            text = qty > 1
              ? format(this.strings.rowEstimateQty, { total: this.#money(item.total), qty, unit: this.#money(item.unit) })
              : format(this.strings.rowEstimate, { amount: this.#money(item.total) });
          } else if (item.status === 'below_min') {
            text = format(this.strings.rowBelowMin, { min: this.#money(Number(data.minPerCard) || 0) });
          }
          badge.textContent = text;
          badge.dataset.status = item.status;
          badge.hidden = false;
        });
      }

      this.#previewDone = true;
      this.#previewStale = false;
      this.previewStaleEl.hidden = true;
      this.previewResultEl.hidden = false;
      const label = this.previewButton?.querySelector('.button-text');
      if (label) label.textContent = this.strings.previewRecalc;
      this.#announce(`${this.strings.estimateHeading}: ${this.#money(estimate.total)}`);
      this.previewResultEl.focus({ preventScroll: true });
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.previewEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    }

    #clearRowEstimates() {
      for (const badge of this.querySelectorAll('[data-ankauf-row-estimate]')) {
        badge.hidden = true;
        badge.textContent = '';
        delete badge.dataset.status;
      }
    }

    /** Nach jeder Änderung an der Liste ist die Vorschau nicht mehr aktuell. */
    #markPreviewStale() {
      if (!this.#previewDone || this.#previewStale || !this.previewStaleEl) return;
      this.#previewStale = true;
      this.previewStaleEl.hidden = false;
      this.#clearRowEstimates();
    }

    /** @param {{message: string, field?: Element|null}[]} errors */
    #showErrors(errors) {
      if (errors.some((error) => error.field && error.field.closest('li[hidden]'))) {
        this.#collapsed = false;
        this.#updateCollapse();
      }
      this.errorList.replaceChildren();
      for (const error of errors) {
        const item = document.createElement('li');
        if (error.field && error.field.id) {
          const link = document.createElement('a');
          link.href = `#${error.field.id}`;
          link.className = 'text-link';
          link.textContent = error.message;
          link.addEventListener('click', (event) => {
            event.preventDefault();
            error.field.focus();
          });
          item.append(link);
        } else {
          item.textContent = error.message;
        }
        error.field?.setAttribute('aria-invalid', 'true');
        this.errorList.append(item);
      }
      this.errorsEl.hidden = false;
      this.errorsEl.focus();
    }

    #hideErrors() {
      this.errorsEl.hidden = true;
      this.errorList.replaceChildren();
    }

    #setBusy(busy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', String(busy));
      const label = this.submitButton.querySelector('.button-text');
      if (!label) return;
      if (busy) {
        this.submitLabel = label.textContent;
        label.textContent = this.strings.sending;
      } else if (this.submitLabel) {
        label.textContent = this.submitLabel;
      }
    }

    #onSubmit = async (event) => {
      event.preventDefault();
      const { ok, payload } = this.#validate();
      if (!ok) return;

      if (!this.endpoint) {
        this.#showErrors([{ message: this.strings.errorConfig }]);
        return;
      }

      this.#controller?.abort();
      this.#controller = new AbortController();
      this.#setBusy(true);
      this.#announce(this.strings.sending);

      try {
        // text/plain vermeidet den CORS-Preflight, den Apps Script nicht beantwortet.
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
          redirect: 'follow',
          signal: this.#controller.signal
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!data || data.ok !== true) {
          const message = data && data.error === 'rate_limited' ? this.strings.errorRate : this.strings.errorSend;
          this.#showErrors([{ message }]);
          return;
        }
        this.#showResult(data);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Ankauf: Senden fehlgeschlagen', error);
        this.#showErrors([{ message: this.strings.errorSend }]);
      } finally {
        this.#setBusy(false);
        this.#announce('');
      }
    };

    /** @param {{reference?: string, estimate?: {total: number, currency?: string, positions: number, matched: number, manual: number, belowMin: number}}} data */
    #showResult(data) {
      const referenceEl = this.querySelector('[data-ankauf-reference]');
      if (referenceEl) referenceEl.textContent = data.reference ? format(this.strings.reference, { reference: data.reference }) : '';

      const estimateEl = this.querySelector('[data-ankauf-estimate]');
      const estimate = data.estimate;
      if (estimateEl && this.showEstimate && estimate && Number.isFinite(estimate.total)) {
        const money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: estimate.currency || 'EUR' });
        this.querySelector('[data-ankauf-estimate-value]').textContent = money.format(estimate.total);
        const facts = this.querySelector('[data-ankauf-estimate-facts]');
        facts.replaceChildren();
        const addFact = (text) => {
          const li = document.createElement('li');
          li.textContent = text;
          facts.append(li);
        };
        addFact(format(this.strings.estimateMatched, { matched: estimate.matched, total: estimate.positions }));
        if (estimate.manual > 0) addFact(format(estimate.manual === 1 ? this.strings.estimateManualOne : this.strings.estimateManual, { count: estimate.manual }));
        if (estimate.belowMin > 0) addFact(format(estimate.belowMin === 1 ? this.strings.estimateBelowMinOne : this.strings.estimateBelowMin, { count: estimate.belowMin }));
        estimateEl.hidden = false;
      } else if (estimateEl) {
        estimateEl.hidden = true;
      }

      this.form.hidden = true;
      this.resultEl.hidden = false;
      const heading = this.querySelector('[data-ankauf-result-heading]');
      heading?.focus();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.resultEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }

    #onRestart = () => {
      this.#collapsed = false;
      this.#previewDone = false;
      this.#previewStale = false;
      if (this.previewResultEl) this.previewResultEl.hidden = true;
      if (this.previewErrorEl) this.previewErrorEl.hidden = true;
      const previewLabel = this.previewButton?.querySelector('.button-text');
      if (previewLabel) previewLabel.textContent = this.strings.previewButton;
      this.form.reset();
      this.rowsList.replaceChildren();
      this.#addRow();
      this.#updateState();
      this.#hideErrors();
      this.resultEl.hidden = true;
      this.form.hidden = false;
      this.form.querySelector('input:not([type="hidden"])')?.focus();
    };

    /** @param {string} message */
    #announce(message) {
      if (!this.statusEl) return;
      this.statusEl.textContent = '';
      requestAnimationFrame(() => {
        this.statusEl.textContent = message;
      });
    }
  }

  customElements.define('ankauf-form', AnkaufForm);
})();
